import { useState, useCallback } from "react";

import { stableSort } from "../../helpers/stableSort";
import { getSorting } from "../../helpers/getSorting";

import { TABLE_PAGINATION_ROWS_PER_PAGE_OPTIONS } from "../../constants/styles";

import { Props } from "./types";

const INITIAL_PAGE = 0;
const INITIAL_ROWS_PER_PAGE = TABLE_PAGINATION_ROWS_PER_PAGE_OPTIONS[0] as number;

export const usePaginationHandler = (props: Props) => {
  const [page, setPage] = useState(INITIAL_PAGE);
  const [isFetchMoreLoading, setIsFetchMoreLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);

  const handleFetchMore = useCallback(
    async (page: number) => {
      if (!props.fetchMore) {
        return;
      }

      setIsFetchMoreLoading(true);

      await props.fetchMore(page);

      setIsFetchMoreLoading(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.fetchMore]
  );

  const sortedRows = stableSort(props.data, getSorting(props.order as SortOrder, props.orderBy as string));

  const from = page * rowsPerPage;
  const to = page * rowsPerPage + rowsPerPage;
  const selectedRows = sortedRows.slice(from, to);
  const rows = rowsPerPage > 0 ? selectedRows : sortedRows;
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, sortedRows.length - from);

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const isNextPage = page < newPage;

    setPage(newPage);

    if (isNextPage) {
      handleFetchMore(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);

    const newPage = Math.floor(parseInt(event.target.value, 10) / INITIAL_ROWS_PER_PAGE - 1);

    handleFetchMore(newPage);
  };

  return {
    page,
    rows,
    from,
    emptyRows,
    rowsPerPage,
    handleChangePage,
    isFetchMoreLoading,
    handleChangeRowsPerPage,
    isLoading: props.isLoading,
  };
};
